import { createContext, useCallback, useContext, useState } from 'react';

const MemoryContext = createContext();

/**
 * Keš za zauzeće memorije na lancu.
 *
 * ZAŠTO POSTOJI INVALIDACIJA, od 06.10.2026
 * -----------------------------------------
 * Goranov nalaz: posle brisanja baze widget je i dalje pokazivao staro zauzeće
 * dok se ne pritisne F5. Lanac tu nije kriv — izmereno istog dana, `destroy`
 * oslobodi memoriju ISTOG TRENUTKA:
 *
 *     pre destroy    mem_usage = 631 700
 *     posle destroy  mem_usage =   3 140      bez ikakvog odlaganja
 *
 * Stajao je keš. Imao je samo vremensku branu od 5 minuta (`useMemory`), a
 * nijedan način da ga neko poništi — pa je broj bio tačan samo slučajno: kad
 * se promeni izabrani nalog ili server, ili kad prođe pet minuta.
 *
 * Zato se brisanje baze i uvoz ne osvežavaju „sami": svaka radnja koja menja
 * zauzeće zove `invalidateMemory()`. Nema novog mehanizma — poziv ide u
 * postojeće blokove koji već posle mutacije osvežavaju strukturu.
 *
 * Briše se CEO keš, ne samo unos tekućeg naloga: ruta `/api/memory/info` uz
 * zauzeće naloga vraća i mrežne ukupne vrednosti, pa posle brisanja nije tačan
 * ni jedan zapamćen unos.
 */
export const MemoryContextProvider = ({ children }) => {

    const [memoryData, setMemoryData] = useState({});

    // Broj poništavanja. Služi kao zavisnost efekta u `useMemory`.
    //
    // Samo brisanje `memoryData` bi danas bilo dovoljno, jer se `valuesToShare`
    // gradi iznova pri svakom crtanju, pa se identitet konteksta promeni i
    // efekat se ponovi. Ali to je slučajnost koja pada prvog dana kad neko
    // doda `useMemo` — a ovde je cena jedan broj.
    const [memoryStamp, setMemoryStamp] = useState(0);

    const invalidateMemory = useCallback(() => {
        setMemoryData({});
        setMemoryStamp((n) => n + 1);
    }, []);

    const valuesToShare = {
        memoryData,
        setMemoryData,
        memoryStamp,
        invalidateMemory
    }

    return (
        <MemoryContext.Provider
            value={valuesToShare}
        >
            {children}
        </MemoryContext.Provider>
    )
}

/**
 * Samo poništavanje keša, bez ičega drugog.
 *
 * Postoji zato što `useMemory()` pri pozivu SAM kreće da čita sa lanca (ima
 * efekat), pa bi pozivalac kome treba samo poništavanje — ekran za brisanje
 * baze, čarobnjak za uvoz — time izazvao još jedan zahtev pri svakom crtanju.
 * Ovo je čista funkcija nad kontekstom.
 *
 * Radi i kad konteksta nema: tada nema ni keša koji bi se poništio, pa
 * pozivalac ne mora da proverava.
 */
export const useInvalidateMemory = () => {
    const ctx = useContext(MemoryContext);
    return ctx?.invalidateMemory || noop;
};

const noop = () => {};

// Export context for use outside the feature
export default MemoryContext;
